/**
 * ============================================================================
 * SET PASSWORD COMPONENT
 * ============================================================================
 * 
 * Optional: Set password for account (allows password login in future)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import LanguageSwitcher from '../common/LanguageSwitcher';

/**
 * Set Password Component
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const SetPassword = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { setPassword, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = language === 'fr' 
        ? 'Le mot de passe est requis' 
        : 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 8 caractères' 
        : 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = language === 'fr' 
        ? 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre' 
        : 'Password must contain at least one uppercase, one lowercase, and one number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = language === 'fr' 
        ? 'Veuillez confirmer le mot de passe' 
        : 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      // Backend expects: password, confirmPassword
      await setPassword({ 
        password: formData.password,
        confirmPassword: formData.confirmPassword 
      });
      
      showSuccess(
        language === 'fr'
          ? 'Mot de passe défini avec succès!'
          : 'Password set successfully!'
      );

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/settings');
      }
    } catch (error) {
      const errorMessage = error?.message || error?.errors?.[0] || 
        (language === 'fr'
          ? 'Échec de la définition du mot de passe'
          : 'Failed to set password');
      showError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl mb-4 shadow-lg">
              <span className="text-3xl">🔐</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Définir un mot de passe' : 'Set Password'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'fr' 
                ? 'Ajoutez un mot de passe pour vous connecter plus facilement' 
                : 'Add a password for easier login'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr' ? (
                <>
                  <strong>Optionnel:</strong> Définir un mot de passe vous permet de vous connecter 
                  avec votre numéro de téléphone ET votre mot de passe. Vous pouvez toujours utiliser 
                  uniquement votre numéro de téléphone.
                </>
              ) : (
                <>
                  <strong>Optional:</strong> Setting a password allows you to login with your 
                  phone number AND password. You can still use just your phone number.
                </>
              )}
            </p>
          </div>

          {/* Password */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Mot de passe' : 'Password'}
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={language === 'fr' ? 'Minimum 8 caractères' : 'Minimum 8 characters'}
                error={errors.password}
                required
                className="w-full text-lg py-3 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {language === 'fr' 
                ? 'Au moins 8 caractères avec majuscule, minuscule et chiffre' 
                : 'At least 8 characters with uppercase, lowercase, and number'}
            </p>
          </div>

          {/* Confirm Password */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder={language === 'fr' ? 'Répétez le mot de passe' : 'Repeat password'}
                error={errors.confirmPassword}
                required
                className="w-full text-lg py-3 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            className="text-lg py-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
          >
            {language === 'fr' ? 'Définir le mot de passe →' : 'Set Password →'}
          </Button>

          {/* Skip Option */}
          <div className="text-center pt-4">
            <button
              type="button"
              onClick={() => {
                if (onSuccess) {
                  onSuccess();
                } else {
                  navigate('/settings');
                }
              }}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline"
            >
              {language === 'fr' ? 'Ignorer pour l\'instant' : 'Skip for now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetPassword;
